import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ThemeProvider } from "./contexts/ThemeContext";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { Toaster } from "./components/ui/sonner";
import LoginPage from "./pages/LoginPage";
import SSOCallbackPage from "./pages/SSOCallbackPage";
import RequesterDashboard from "./pages/RequesterDashboard";
import SecurityDashboard from "./pages/SecurityDashboard";
import AdminPanel from "./pages/AdminPanel";
import ArchivePage from "./pages/ArchivePage";
import PermanentPassesPage from "./pages/PermanentPassesPage";

// Protected route wrapper
function ProtectedRoute({ children, allowedRoles }) {
  const { isAuthenticated, user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">Загрузка...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user?.role)) {
    // Redirect to appropriate dashboard based on role
    if (user?.role === 'admin') return <Navigate to="/admin" replace />;
    if (user?.role === 'security') return <Navigate to="/security" replace />;
    if (user?.role === 'orp') return <Navigate to="/dashboard" replace />;
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

// Auth redirect
function AuthRoute({ children }) {
  const { isAuthenticated, user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">Загрузка...</div>
      </div>
    );
  }

  if (isAuthenticated) {
    if (user?.role === 'admin') return <Navigate to="/admin" replace />;
    if (user?.role === 'security') return <Navigate to="/security" replace />;
    if (user?.role === 'orp') return <Navigate to="/dashboard" replace />;
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public route */}
      <Route path="/login" element={
        <AuthRoute>
          <LoginPage />
        </AuthRoute>
      } />

      {/* SSO Callback route */}
      <Route path="/sso/callback" element={<SSOCallbackPage />} />

      {/* Requester (user/orp) routes */}
      <Route path="/dashboard" element={
        <ProtectedRoute allowedRoles={['user', 'orp', 'admin']}>
          <RequesterDashboard />
        </ProtectedRoute>
      } />

      {/* Security routes */}
      <Route path="/security" element={
        <ProtectedRoute allowedRoles={['security', 'admin']}>
          <SecurityDashboard />
        </ProtectedRoute>
      } />

      {/* Admin routes */}
      <Route path="/admin" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <AdminPanel />
        </ProtectedRoute>
      } />

      {/* Permanent passes - accessible by admin, security, orp */}
      <Route path="/permanent-passes" element={
        <ProtectedRoute allowedRoles={['admin', 'security', 'orp']}>
          <PermanentPassesPage />
        </ProtectedRoute>
      } />

      {/* Archive - accessible by all authenticated users */}
      <Route path="/archive" element={
        <ProtectedRoute>
          <ArchivePage />
        </ProtectedRoute>
      } />

      {/* Default redirect */}
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
